'use client';

import { useEffect } from 'react';
import Script from 'next/script';
import { initExitTracking } from '@/utils/analytics';

/**
 * Analytics Component
 * Handles Google Tag Manager, MarketingCloudFX, and exit tracking initialization
 */
export default function Analytics() {
  useEffect(() => {
    // Defer non-critical tracking initialization to reduce TBT
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => initExitTracking(), { timeout: 2000 });
    } else {
      setTimeout(() => initExitTracking(), 1000);
    }
  }, []);

  return (
    <>
      {/* Google Tag Manager */}
      <Script
        id="gtm-script"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-PDCXBT7B');
          `,
        }}
      />

      {/* MarketingCloudFX Tracking */}
      <Script
        id="mcfx-script"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,o,u,a,m){
              w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments);},
              w[o].e=1*new Date();w[o].u=u;a=d.createElement('script'),
              m=d.getElementsByTagName('script')[0];a.async=1;
              a.src=u+'/mcfx.js';m.parentNode.insertBefore(a,m);
            })(window,document,'mcfx','https://t.marketingcloudfx.com');
            mcfx('create', 20008);
          `,
        }}
      />

      {/* MarketingCloudFX URL Parameters Tracking */}
      <Script
        id="mcfx-url-params"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
            (function() {
              function process() {
                window.RCFX_SERIALIZED_URL_PARAMS = window.RCFX_SERIALIZED_URL_PARAMS || '';
                window.mcfx(function(tracker) {
                  const urlParams = {
                    utm_source: tracker.visitor.get('utms') || '',
                    utm_medium: tracker.visitor.get('utmm') || '',
                    utm_campaign: tracker.visitor.get('utmc') || '',
                    gclid: tracker.visitor.get('gclid') || '',
                    gbraid: tracker.visitor.get('gbraid') || '',
                    wbraid: tracker.visitor.get('wbraid') || '',
                    msclkid: tracker.visitor.get('msclkid') || '',
                    fbclid: tracker.visitor.get('fbclid') || '',
                    twclid: tracker.visitor.get('twclid') || '',
                    fx_uid: tracker.visitor.get('uid') || '',
                    fx_sid: tracker.visitor.get('sid') || '',
                    fx_source: tracker.visitor.get('s') || '',
                    fx_medium: tracker.visitor.get('m') || '',
                    fx_referrer: tracker.visitor.get('r') || '',
                  };
                  window.RCFX_SERIALIZED_URL_PARAMS = Object.keys(urlParams)
                    .map(function(key) {
                      return key + '=' + urlParams[key];
                    })
                    .join('&');
                });
              }

              function mcfxEnabled() {
                var status = 'inactive';
                if (typeof window.mcfx === 'undefined') {
                  return false;
                }
                window.mcfx(function(t) {
                  status = t.configuration.status;
                });
                return status === 'active';
              }

              if (mcfxEnabled()) {
                process();
              } else {
                window.addEventListener('mcfx:initialized', process);
              }
            })();
          `,
        }}
      />

      {/* GTM noscript fallback */}
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-PDCXBT7B"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    </>
  );
}
